import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { environment } from '../../../../../environments/environment';
import { LocalizationService } from '../../../../services/localization.service';
import { faCaretLeft, faCaretRight } from '@fortawesome/free-solid-svg-icons';
import { CalendarService } from '../../services/calendar.service';

@Component({
  selector: 'app-time-slot-calendar',
  templateUrl: './time-slot-calendar.component.html',
  styleUrls: ['./time-slot-calendar.component.css'],
})
export class TimeSlotCalendarComponent implements OnInit {

  @Input()
  toContainPrevMonth = true;

  @Input()
  toContainNextMonth = true;

  @Input()
  calendarFixedSize = true;

  @Input()
  calendarService: CalendarService;

  @Output() dateSelected: EventEmitter<any> = new EventEmitter<any>();
  @Output() monthChanged: EventEmitter<any> = new EventEmitter<any>();

  faCaretLeft = faCaretLeft;
  faCaretRight = faCaretRight;

  _calendarTableCache: any = null;

  _selectedDate: Date;

  constructor(
    private localizationService: LocalizationService,
  ) {
  }

  ngOnInit() {
  }

  get selectedDate(): Date {
    return this.calendarService.selectedDate;
  }

  get selectedDateOrToday(): Date {
    return this.calendarService.selectedDate ? this.calendarService.selectedDate : this._today;
  }

  get firstDayOfWeek(): number {
    return this.localizationService.firstDayOfWeek;
  }

  get daysOfWeek(): string[] {
    const dow = [];
    for (let i = this.firstDayOfWeek; i < 7 + this.firstDayOfWeek; i++) {
      dow.push(this.localizationService.daysOfWeekShort[i < 7 ? i : i - 7]);
    }

    return dow;
  }

  private get _today(): Date {
    return new Date();
  }

  get showYear(): boolean {
    return this._today.getFullYear() !== this.selectedDateOrToday.getFullYear();
  }

  isDateEnabled(date: Date): boolean {
    return this.calendarService.isDateEnabled(date);
  }

  isToday(day): boolean {
    return day.date === this._today.getDate() && day.month === (this._today.getMonth() + 1) ? true : false;
  }

  isThisMonth(day): boolean {
    return day.month === this._calendarMonth;
  }


  isSelected(day): boolean {
    return this.selectedDate && day.date === this.selectedDate.getDate() &&
      day.month === (this.selectedDate.getMonth() + 1) &&
      this._calendarMonth === (this.selectedDate.getMonth() + 1)
      ? true : false;
  }

  get calendarYear(): number {
    return this.selectedDateOrToday.getFullYear();
  }

  private get _calendarMonth(): number {
    return this.selectedDateOrToday.getMonth() + 1;
  }

  private get _calendarDay(): number {
    return this.selectedDateOrToday.getDate();
  }

  get monthName(): string {
    return this.localizationService.getMonthNameLongByDate(this.selectedDateOrToday);
  }

  private get _lastDayOfWeek() {
    return (this.firstDayOfWeek + 6) % 7;
  }

  // Month 1-12
  private _getLastDay(month, year): number {
    const d = new Date(year, month, 0);
    return d.getDate();
  }

  get canBackwardMonth(): boolean {
    return this.selectedDateOrToday.getFullYear() > this._today.getFullYear() || this.selectedDateOrToday.getMonth() > this._today.getMonth();
  }

  shiftMonth(offset: number) {
    if (!this.selectedDate || !offset) {
      return;
    }

    // first day next month, forward search free slot
    // or last day prev month
    const newMonth = offset > 0 ?
      new Date(this.selectedDate.getFullYear(), this.selectedDate.getMonth() + offset, 1) :
      new Date(this.selectedDate.getFullYear(), this.selectedDate.getMonth() + offset + 1, 0);

    this.monthChanged.emit(newMonth);
  }

  onSelectDate(date) {
    if (!date.disabled) {
      if (date.date !== '') {
        const selectedDate = new Date(this.calendarYear, this._calendarMonth - 1, date.date);
        this.dateSelected.emit(selectedDate);
      }
    }
  }

  get calendarTable(): any {
    const newSelectedDate = this.selectedDate;
    if (this._calendarTableCache == null ||
      (newSelectedDate && this._selectedDate && newSelectedDate.getTime() !== this._selectedDate.getTime())) {
      this._selectedDate = newSelectedDate;
      this._calendarTableCache = this._calendarTable;
    }

    return this._calendarTableCache;
  }

  get _calendarTable(): any {
    const year = this.calendarYear;
    const month = this._calendarMonth;

    const tempLastDate = this._getLastDay(month, year);
    const currentDays = [];
    for (let i = 1; i <= tempLastDate; i++) {
      const currentDate = new Date(year, month - 1, i);
      const selected = i !== this._calendarDay ? false : true;
      currentDays.push({ 'month': month, 'date': i, 'disabled': !this.isDateEnabled(currentDate), 'selected': selected, 'empty': false });
    }

    // Determine Date of First of the Month
    const firstDate = new Date(year, month - 1, 1);
    const lastDate = new Date(year, month - 1, tempLastDate);
    let firstDateDayOfWeek = firstDate.getDay();
    const lastDateDayOfWeek = lastDate.getDay();

    // Prepend Prev Month Dates
    const prevDays = [];
    if (firstDateDayOfWeek !== this.firstDayOfWeek) {
      let prevLast = this._getLastDay(month - 1, year);
      firstDateDayOfWeek = firstDateDayOfWeek === 0 ? 7 : firstDateDayOfWeek;
      for (let i = this.firstDayOfWeek; i < firstDateDayOfWeek; i++) {
        if (this.toContainPrevMonth) {
          prevDays.push({ 'month': month - 1, 'date': prevLast, 'disabled': true, 'selected': false, 'empty': false });
        } else {
          prevDays.push({ 'month': '', 'date': '', 'disabled': true, 'selected': false, 'empty': true });
        }
        prevLast--;
      }
    }

    const completeDays = prevDays.reverse().concat(currentDays);
    // Zero to last day of week
    if (lastDateDayOfWeek !== this._lastDayOfWeek) {
      // Not Saturday
      let nIndex = 1;

      for (let i = 7; i > lastDateDayOfWeek; i--) {
        if (this.toContainNextMonth) {
          completeDays.push({ 'month': month + 1, 'date': nIndex, disabled: true, 'selected': false, 'empty': false });
        } else {
          completeDays.push({ 'month': '', 'date': '', disabled: false, 'selected': false, 'empty': true });
        }
        nIndex++;
      }
    }

    let weekDays = [];
    const weekRows = [];
    // let i = 1; i < firstDate.getDay(); i++
    for (let i = 0; i < completeDays.length; i++) {
      const dayOfWeek = (i + this.firstDayOfWeek) % 7;
      // if (this._disabledDaysOfWeek.indexOf(dayOfWeek) > -1) {
      //   completeDays[i].disabled = true;
      // }
      if (dayOfWeek === 0) {
        weekDays.push(completeDays[i]);
        weekRows.push(weekDays);
        weekDays = [];
      } else {
        weekDays.push(completeDays[i]);
      }
    }

    return weekRows;
  }
}
